import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { PageContainer } from '../../components/AdminContentWrapper';
import UserProfileService from '../../../../services/UserProfileService';
import SemanticWorkspaceService from '../../../../services/SemanticWorkspaceService';
import SemanticModelService from '../../../../services/SemanticModelService';
import WorkspaceTable from './components/WorkspaceTable';
import WorkspaceDetailDialog from './components/WorkspaceDetailDialog';
import WorkspaceFormDialog from './components/WorkspaceFormDialog';
import WorkspaceDeleteDialog from './components/WorkspaceDeleteDialog';
import WorkspaceUsersDialog from './components/WorkspaceUsersDialog';
import WorkspaceModelsDialog from './components/WorkspaceModelsDialog';
import WorkspaceConversationsDialog from './components/WorkspaceConversationsDialog';

const resolveUserId = (profile) =>
  profile?.user_id || profile?.userId || profile?.user_uuid || profile?.sub || null;

const resolveAccountId = (profile) =>
  profile?.account_id ||
  profile?.accountId ||
  profile?.account_uuid ||
  profile?.accountUuid ||
  profile?.accountUUID ||
  null;

const normalizeModelRecord = (record, fallbackId) => {
  if (!record || typeof record !== 'object') {
    return {
      semantic_model_id: fallbackId,
      name: fallbackId,
      description: 'No fue posible recuperar los detalles del modelo.',
      domain: '—',
      version: '—'
    };
  }

  const semanticModelId =
    record.semantic_model_id ||
    record.semanticModelId ||
    record.id ||
    record.ID ||
    fallbackId;

  return {
    semantic_model_id: semanticModelId,
    name: record.name || record.model_name || semanticModelId,
    description: record.description || '',
    domain: record.domain || record.domain_name || '—',
    version: record.version || record.version_id || record.versionId || '—'
  };
};

const extractModelRecord = (response) => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  if (response.semantic_model_id || response.semanticModelId) {
    return response;
  }

  if (response.data) {
    return extractModelRecord(response.data);
  }

  if (response.item) {
    return extractModelRecord(response.item);
  }

  if (Array.isArray(response.items) && response.items.length > 0) {
    return extractModelRecord(response.items[0]);
  }

  return null;
};

const SemanticWorkspace = () => {
  const [profile, setProfile] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [viewWorkspace, setViewWorkspace] = useState(null);
  const [editWorkspace, setEditWorkspace] = useState(null);
  const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);

  const [deleteState, setDeleteState] = useState({ open: false, mode: 'soft', workspace: null });
  const [isProcessingDelete, setIsProcessingDelete] = useState(false);

  const [usersDialog, setUsersDialog] = useState({ open: false, workspace: null });
  const [modelsDialog, setModelsDialog] = useState({ open: false, workspace: null, manage: false });
  const [conversationsDialog, setConversationsDialog] = useState({ open: false, workspace: null });

  const currentUserId = useMemo(() => resolveUserId(profile), [profile]);

  const hydrateModelDetails = useCallback(async (modelIds = []) => {
    if (!modelIds || modelIds.length === 0) {
      return [];
    }
    const uniqueIds = [...new Set(modelIds)];
    const details = await Promise.all(
      uniqueIds.map(async (modelId) => {
        try {
          const response = await SemanticModelService.getById(modelId);
          const record = extractModelRecord(response) || { semantic_model_id: modelId };
          return normalizeModelRecord(record, modelId);
        } catch (err) {
          console.error('SemanticWorkspace.hydrateModelDetails: error obteniendo modelo', modelId, err);
          return normalizeModelRecord(null, modelId);
        }
      })
    );
    return details;
  }, []);

  const updateWorkspaceInState = useCallback((updatedWorkspace) => {
    if (!updatedWorkspace?.semantic_workspace_id) {
      return;
    }
    setWorkspaces((previous) => {
      const index = previous.findIndex(
        (item) => item.semantic_workspace_id === updatedWorkspace.semantic_workspace_id
      );
      if (index === -1) {
        return [...previous, updatedWorkspace];
      }
      const next = [...previous];
      next[index] = updatedWorkspace;
      return next;
    });
  }, []);

  const removeWorkspaceFromState = useCallback((workspaceId) => {
    setWorkspaces((previous) =>
      previous.filter((item) => item.semantic_workspace_id !== workspaceId)
    );
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const userProfile = await UserProfileService.getProfile();
      setProfile(userProfile);

      const resolvedUserId = resolveUserId(userProfile);
      if (!resolvedUserId) {
        throw new Error('No fue posible determinar el usuario autenticado.');
      }

      const response = await SemanticWorkspaceService.listByUser(resolvedUserId, {
        includeDeleted: true
      });

      setWorkspaces(response.items || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('SemanticWorkspace.loadInitialData: error cargando datos', err);
      setError(err.message || 'No fue posible cargar los espacios de análisis.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleRefresh = () => {
    loadInitialData();
  };

  const handleView = (workspace) => {
    setViewWorkspace(workspace);
  };

  const handleEdit = (workspace) => {
    setEditWorkspace(workspace);
  };

  const handleCreateWorkspaceOpen = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async (formValues) => {
    const accountId = resolveAccountId(profile);

    if (!currentUserId || !accountId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar al usuario o cuenta actual para crear el espacio.',
        severity: 'error'
      });
      return;
    }

    const trimmedName = formValues.name?.trim();
    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: 'El nombre del espacio es obligatorio.',
        severity: 'error'
      });
      return;
    }

    try {
      setIsCreatingWorkspace(true);
      const created = await SemanticWorkspaceService.create({
        account_id: accountId,
        user_owner_id: currentUserId,
        name: trimmedName,
        description: formValues.description?.trim() || undefined,
        business_area: formValues.business_area?.trim() || undefined,
        is_active: formValues.is_active !== false,
        models_linked: [],
        conversations: [],
        users_allowed: [{ user_id: currentUserId }]
      });

      updateWorkspaceInState(created);
      setCreateDialogOpen(false);
      setLastUpdated(new Date());
      setSnackbar({
        open: true,
        message: 'Espacio de análisis creado correctamente.',
        severity: 'success'
      });
    } catch (err) {
      console.error('SemanticWorkspace.handleCreateSubmit: error', err);
      setSnackbar({
        open: true,
        message: err.message || 'No fue posible crear el espacio de análisis.',
        severity: 'error'
      });
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  const handleEditSubmit = async (payload) => {
    if (!payload?.semantic_workspace_id) {
      return;
    }

    const { semantic_workspace_id: workspaceId, change_reason, ...rest } = payload;

    try {
      setIsSavingWorkspace(true);
      const updated = await SemanticWorkspaceService.update(workspaceId, {
        ...rest,
        semantic_workspace_id: workspaceId,
        ...(change_reason
          ? {
              audit: {
                change_reason
              }
            }
          : {})
      });
      updateWorkspaceInState(updated);
      setLastUpdated(new Date());
      setEditWorkspace(null);
      setSnackbar({
        open: true,
        message: 'Espacio de análisis actualizado correctamente.',
        severity: 'success'
      });
    } catch (err) {
      console.error('SemanticWorkspace.handleEditSubmit: error', err);
      setSnackbar({
        open: true,
        message: err.message || 'No fue posible actualizar el espacio de análisis.',
        severity: 'error'
      });
    } finally {
      setIsSavingWorkspace(false);
    }
  };

  const handleSoftDelete = (workspace) => {
    setDeleteState({ open: true, mode: 'soft', workspace });
  };

  const handleRestore = async (workspace) => {
    if (!workspace?.semantic_workspace_id) {
      return;
    }

    try {
      setIsProcessingDelete(true);
      const restored = await SemanticWorkspaceService.restore(workspace.semantic_workspace_id);
      updateWorkspaceInState(restored);
      setLastUpdated(new Date());
      setSnackbar({
        open: true,
        message: 'El espacio fue restaurado correctamente.',
        severity: 'success'
      });
    } catch (err) {
      console.error('SemanticWorkspace.handleRestore: error', err);
      setSnackbar({
        open: true,
        message: err.message || 'No se pudo restaurar el espacio.',
        severity: 'error'
      });
    } finally {
      setIsProcessingDelete(false);
    }
  };

  const handleHardDelete = (workspace) => {
    setDeleteState({ open: true, mode: 'hard', workspace });
  };

  const handleDeleteConfirm = async (workspace, mode) => {
    if (!workspace?.semantic_workspace_id) {
      return;
    }
    const workspaceId = workspace.semantic_workspace_id;

    try {
      setIsProcessingDelete(true);
      if (mode === 'hard') {
        await SemanticWorkspaceService.remove(workspaceId);
        removeWorkspaceFromState(workspaceId);
        setLastUpdated(new Date());
        setSnackbar({
          open: true,
          message: 'Espacio eliminado permanentemente.',
          severity: 'success'
        });
      } else {
        const updated = await SemanticWorkspaceService.softDelete(workspaceId, {
          deleted: true,
          change_reason: 'soft_delete_from_admin_ui'
        });
        updateWorkspaceInState(updated);
        setLastUpdated(new Date());
        setSnackbar({
          open: true,
          message: 'Espacio marcado como eliminado (soft).',
          severity: 'success'
        });
      }
    } catch (err) {
      console.error('SemanticWorkspace.handleDeleteConfirm: error', err);
      setSnackbar({
        open: true,
        message: err.message || 'No fue posible completar la acción solicitada.',
        severity: 'error'
      });
    } finally {
      setIsProcessingDelete(false);
      setDeleteState({ open: false, mode: 'soft', workspace: null });
    }
  };

  const handleShowModels = (workspace) => {
    setModelsDialog({ open: true, workspace, manage: false });
  };

  const handleManageModels = (workspace) => {
    setModelsDialog({ open: true, workspace, manage: true });
  };

  const handleShowConversations = (workspace) => {
    setConversationsDialog({ open: true, workspace });
  };

  const handleManageUsers = (workspace) => {
    setUsersDialog({ open: true, workspace });
  };

  const fetchWorkspaceUsers = useCallback(async (workspace) => {
    if (!workspace?.semantic_workspace_id) {
      return [];
    }
    const users = await SemanticWorkspaceService.getUsers(workspace.semantic_workspace_id);
    updateWorkspaceInState({
      ...workspace,
      users_allowed: users
    });
    setLastUpdated(new Date());
    return users;
  }, [updateWorkspaceInState]);

  const addWorkspaceUser = useCallback(
    async (workspace, userId) => {
      if (!workspace?.semantic_workspace_id) {
        return [];
      }
      const users = await SemanticWorkspaceService.addUsers(workspace.semantic_workspace_id, userId);
      updateWorkspaceInState({
        ...workspace,
        users_allowed: users
      });
      setLastUpdated(new Date());
      return users;
    },
    [updateWorkspaceInState]
  );

  const removeWorkspaceUser = useCallback(
    async (workspace, userId) => {
      if (!workspace?.semantic_workspace_id) {
        return [];
      }
      const users = await SemanticWorkspaceService.removeUser(workspace.semantic_workspace_id, userId);
      updateWorkspaceInState({
        ...workspace,
        users_allowed: users
      });
      setLastUpdated(new Date());
      return users;
    },
    [updateWorkspaceInState]
  );

  const fetchWorkspaceModels = useCallback(
    async (workspace) => {
      if (!workspace?.semantic_workspace_id) {
        return [];
      }
      const ids = await SemanticWorkspaceService.getModels(workspace.semantic_workspace_id);
      updateWorkspaceInState({
        ...workspace,
        models_linked: ids
      });
      setLastUpdated(new Date());
      return hydrateModelDetails(ids);
    },
    [hydrateModelDetails, updateWorkspaceInState]
  );

  const addWorkspaceModel = useCallback(
    async (workspace, modelId) => {
      if (!workspace?.semantic_workspace_id) {
        return [];
      }
      const ids = await SemanticWorkspaceService.addModels(workspace.semantic_workspace_id, modelId);
      updateWorkspaceInState({
        ...workspace,
        models_linked: ids
      });
      setLastUpdated(new Date());
      return hydrateModelDetails(ids);
    },
    [hydrateModelDetails, updateWorkspaceInState]
  );

  const removeWorkspaceModel = useCallback(
    async (workspace, modelId) => {
      if (!workspace?.semantic_workspace_id) {
        return [];
      }
      const ids = await SemanticWorkspaceService.removeModel(workspace.semantic_workspace_id, modelId);
      updateWorkspaceInState({
        ...workspace,
        models_linked: ids
      });
      setLastUpdated(new Date());
      return hydrateModelDetails(ids);
    },
    [hydrateModelDetails, updateWorkspaceInState]
  );

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <PageContainer
      title="Espacios de análisis"
      description="Administra los espacios de análisis asignados al usuario, gestiona usuarios, modelos vinculados y su ciclo de vida."
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
            sx={{
              borderColor: 'var(--wb-blue-40)',
              color: 'var(--wb-blue)',
              '&:hover': { borderColor: 'var(--wb-blue)' }
            }}
          >
            Actualizar
          </Button>
        </Stack>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar los espacios de análisis</AlertTitle>
          {error}
        </Alert>
      )}

      {!error && !currentUserId && !isLoading && (
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Usuario no identificado</AlertTitle>
        No pudimos determinar el usuario actual. Verifica tu sesión e intenta nuevamente.
      </Alert>
    )}

      <Box
        sx={{
          backgroundColor: 'var(--wb-bg-panel)',
          borderRadius: 2,
          border: '1px solid var(--wb-border)',
          p: { xs: 2, sm: 3 },
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, md: 2 }
        }}
      >
        <Box>
          <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600, fontSize: '1rem' }}>
            Gestiona tus espacios de análisis
          </Typography>
          <Typography sx={{ color: 'var(--wb-muted-75)', mt: 0.5 }}>
            Crea nuevos espacios para organizar contextos y equipos de trabajo.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateWorkspaceOpen}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
          >
            Agregar espacio de análisis
          </Button>
        </Stack>
      </Box>

      <WorkspaceTable
        rows={workspaces}
        isLoading={isLoading}
        onView={handleView}
        onEdit={handleEdit}
        onSoftDelete={handleSoftDelete}
        onRestore={handleRestore}
        onDelete={handleHardDelete}
        onManageUsers={handleManageUsers}
        onManageModels={handleManageModels}
        onShowModels={handleShowModels}
        onShowConversations={handleShowConversations}
      />

      <Typography variant="caption" sx={{ color: 'var(--wb-muted-70)', display: 'block', mt: 2 }}>
        Última actualización visualizada: {lastUpdated ? lastUpdated.toLocaleString() : '—'}
      </Typography>

      <WorkspaceDetailDialog
        open={Boolean(viewWorkspace)}
        workspace={viewWorkspace}
        onClose={() => setViewWorkspace(null)}
      />

      <WorkspaceFormDialog
        open={createDialogOpen}
        mode="create"
        workspace={null}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
        isSubmitting={isCreatingWorkspace}
      />

      <WorkspaceFormDialog
        open={Boolean(editWorkspace)}
        mode="edit"
        workspace={editWorkspace}
        onClose={() => setEditWorkspace(null)}
        onSubmit={handleEditSubmit}
        isSubmitting={isSavingWorkspace}
      />

      <WorkspaceDeleteDialog
        open={deleteState.open}
        mode={deleteState.mode}
        workspace={deleteState.workspace}
        onClose={() => setDeleteState({ open: false, mode: 'soft', workspace: null })}
        onConfirm={(workspaceSelected) => handleDeleteConfirm(workspaceSelected, deleteState.mode)}
        isProcessing={isProcessingDelete}
      />

      <WorkspaceUsersDialog
        open={usersDialog.open}
        workspace={usersDialog.workspace}
        onClose={() => setUsersDialog({ open: false, workspace: null })}
        fetchUsers={fetchWorkspaceUsers}
        addUsers={addWorkspaceUser}
        removeUser={removeWorkspaceUser}
      />

      <WorkspaceModelsDialog
        open={modelsDialog.open}
        workspace={modelsDialog.workspace}
        allowManage={modelsDialog.manage}
        onClose={() => setModelsDialog({ open: false, workspace: null, manage: false })}
        fetchModels={fetchWorkspaceModels}
        addModel={modelsDialog.manage ? addWorkspaceModel : undefined}
        removeModel={modelsDialog.manage ? removeWorkspaceModel : undefined}
      />

      <WorkspaceConversationsDialog
        open={conversationsDialog.open}
        workspace={conversationsDialog.workspace}
        onClose={() => setConversationsDialog({ open: false, workspace: null })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success'
                ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning'
                ? 'var(--wb-warning-90)'
                : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

export default SemanticWorkspace;
